import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CompanyCombobox } from "@/components/parcelio/company-combobox";
import {
  vehiclesApi,
  directoryApi,
  type Vehicle,
  type VehicleType as ApiVehicleType,
  type DriverDirectoryEntry,
  type CorporateAccount,
} from "@/lib/api";
import { useAuth } from "@/stores/auth";
import {
  Plus,
  Search,
  Truck,
  Bike,
  Car,
  Pencil,
  Trash2,
  Copy,
  Gauge,
  Wrench,
  Zap,
  Fuel,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock, Spinner } from "@/components/ui/spinner";

type VehicleStatus = Vehicle["status"];

const statusStyles: Record<VehicleStatus, string> = {
  active: "bg-success/10 text-success border-success/20",
  maintenance: "bg-warning/15 text-warning-foreground border-warning/30",
  inactive: "bg-info/10 text-info border-info/20",
  retired: "bg-muted text-muted-foreground border-border",
};
const statusLabels: Record<VehicleStatus, string> = {
  active: "Active",
  maintenance: "In service",
  inactive: "Inactive",
  retired: "Retired",
};

function typeIconFor(name: string | undefined) {
  const n = (name ?? "").toLowerCase();
  if (n.includes("bike") || n.includes("moto")) return Bike;
  if (n.includes("car")) return Car;
  return Truck;
}

type FormState = {
  plate: string;
  type_id: string;
  make: string;
  model: string;
  year: number;
  capacity_kg: number;
  status: VehicleStatus;
  current_driver_id: string | undefined;
  odometer_km: number;
  next_service_km: number;
  fuel_type: string;
  corporate_account_id: string;
};

function emptyForm(defaultTypeId: string): FormState {
  return {
    plate: "",
    type_id: defaultTypeId,
    make: "",
    model: "",
    year: new Date().getFullYear(),
    capacity_kg: 25,
    status: "active",
    current_driver_id: undefined,
    odometer_km: 0,
    next_service_km: 5000,
    fuel_type: "petrol",
    corporate_account_id: "",
  };
}

export default function VehiclesPage() {
  const token = useAuth((s) => s.session?.accessToken);
  const session = useAuth((s) => s.session);
  const isPersonal = session?.kind === "personal";
  const isCorporate = session?.kind === "corporate";
  const isClient = session?.kind === "client";
  const roles = session?.roles ?? [];
  const isDriverOnly = session?.kind === "staff" && roles.length > 0 && roles.every((r) => r === "driver");

  const [items, setItems] = useState<Vehicle[]>([]);
  const [types, setTypes] = useState<ApiVehicleType[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [corporates, setCorporates] = useState<CorporateAccount[]>([]);
  const [loading, setLoading] = useState(true);

  // Locked to the logged-in corporate's own company — resolved via
  // /corporates/me (server-side, can't be spoofed). Backend forces this
  // regardless; this is just to lock/prefill the UI to match.
  const [ownCorporateId, setOwnCorporateId] = useState<string | undefined>(undefined);

  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | string>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | VehicleStatus>("all");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm(""));
  const [toDelete, setToDelete] = useState<Vehicle | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        // The full corporate directory is staff-only server-side — a
        // corporate/personal session never needed it here (they can't
        // reassign a vehicle to another company), so skip it for them
        // rather than letting it 403 the whole load and blank out their
        // own (correctly-scoped) vehicle list. Same for the full driver
        // directory, which is denied outright for driver-only logins (no
        // reassignment UI for a driver anyway).
        const [vehicleList, typeList, driverList, corporateList] = await Promise.all([
          vehiclesApi.list(token),
          vehiclesApi.listTypes(),
          isDriverOnly ? Promise.resolve([]) : directoryApi.drivers(token),
          isCorporate || isPersonal || isClient || isDriverOnly ? Promise.resolve([]) : directoryApi.corporates(token),
        ]);
        if (cancelled) return;
        setItems(vehicleList);
        setTypes(typeList);
        setDrivers(driverList);
        setCorporates(corporateList);
        if (isCorporate) {
          directoryApi.myCorporate(token).then((mine) => {
            if (!cancelled) setOwnCorporateId(mine.id);
          }).catch(() => {});
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load vehicles");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const typesById = useMemo(() => new Map(types.map((t) => [t.id, t])), [types]);
  const driversById = useMemo(() => new Map(drivers.map((d) => [d.id, d])), [drivers]);

  const filtered = useMemo(() => {
    return items.filter((v) => {
      if (typeFilter !== "all" && v.type_id !== typeFilter) return false;
      if (statusFilter !== "all" && v.status !== statusFilter) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (
        v.plate.toLowerCase().includes(s) ||
        (v.model ?? "").toLowerCase().includes(s) ||
        v.id.toLowerCase().includes(s)
      );
    });
  }, [items, q, typeFilter, statusFilter]);

  const counts = useMemo(
    () => ({
      active: items.filter((v) => v.status === "active").length,
      maintenance: items.filter((v) => v.status === "maintenance").length,
      inactive: items.filter((v) => v.status === "inactive").length,
      retired: items.filter((v) => v.status === "retired").length,
    }),
    [items],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm(types[0]?.id ?? ""), corporate_account_id: ownCorporateId ?? "" });
    setEditorOpen(true);
  };

  // Dashboard "Add vehicle" quick action links here with ?new=1 to jump
  // straight into the create dialog instead of landing on the bare list.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!loading && searchParams.get("new") === "1") {
      openCreate();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  const openEdit = (v: Vehicle) => {
    setEditing(v);
    setForm({
      plate: v.plate,
      type_id: v.type_id,
      make: v.make ?? "",
      model: v.model ?? "",
      year: v.year ?? new Date().getFullYear(),
      capacity_kg: v.capacity_kg ?? 0,
      status: v.status,
      current_driver_id: v.current_driver_id ?? undefined,
      odometer_km: v.odometer_km ?? 0,
      next_service_km: v.next_service_km ?? 0,
      fuel_type: v.fuel_type ?? "petrol",
      corporate_account_id: ownCorporateId ?? v.corporate_account_id ?? "",
    });
    setEditorOpen(true);
  };

  const duplicate = async (v: Vehicle) => {
    try {
      const created = await vehiclesApi.create(
        {
          type_id: v.type_id,
          plate: `${v.plate}-COPY`,
          make: v.make ?? undefined,
          model: v.model ?? undefined,
          year: v.year ?? undefined,
          capacity_kg: v.capacity_kg ?? undefined,
          status: "inactive",
          odometer_km: v.odometer_km ?? undefined,
          next_service_km: v.next_service_km ?? undefined,
          fuel_type: v.fuel_type ?? undefined,
        },
        token,
      );
      setItems((cur) => [created, ...cur]);
      toast.success(`Duplicated ${v.plate}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to duplicate vehicle");
    }
  };

  const save = async () => {
    if (!form.plate.trim() || !form.type_id) {
      toast.error("Plate and type are required");
      return;
    }
    if (!isPersonal && !isCorporate && !form.corporate_account_id) {
      toast.error("Select the company this vehicle belongs to");
      return;
    }
    setSaving(true);
    try {
      const body = {
        type_id: form.type_id,
        plate: form.plate,
        make: form.make || undefined,
        model: form.model || undefined,
        year: form.year,
        capacity_kg: form.capacity_kg,
        status: form.status,
        current_driver_id: form.current_driver_id ?? null,
        odometer_km: form.odometer_km,
        next_service_km: form.next_service_km,
        fuel_type: form.fuel_type,
        corporate_account_id: form.corporate_account_id || undefined,
      };
      if (editing) {
        const updated = await vehiclesApi.update(editing.id, body, token);
        setItems((cur) => cur.map((v) => (v.id === editing.id ? updated : v)));
        toast.success(`Updated ${form.plate}`);
      } else {
        const created = await vehiclesApi.create(body, token);
        setItems((cur) => [created, ...cur]);
        toast.success(`Added ${form.plate}`);
      }
      setEditorOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save vehicle");
    } finally {
      setSaving(false);
    }
  };

  // NOTE: the backend has no DELETE /vehicles/:id endpoint yet, so this only
  // removes the vehicle from local UI state — it is not persisted server-side.
  const confirmDelete = () => {
    if (!toDelete) return;
    setItems((cur) => cur.filter((v) => v.id !== toDelete.id));
    toast.success(`Removed ${toDelete.plate} (local only — no backend delete endpoint yet)`);
    setToDelete(null);
  };

  return (
    <>
      <Topbar
        title="Vehicles"
        subtitle={`${items.length} in fleet · ${counts.active} active`}
        actions={
          isDriverOnly ? undefined : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/vehicles/assignments">Assignments</Link>
              </Button>
              <Button size="sm" onClick={openCreate}>
                <Plus className="mr-1.5 h-4 w-4" /> Add vehicle
              </Button>
            </div>
          )
        }
      />

      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-4">
          {(["active", "maintenance", "inactive", "retired"] as const).map((s) => (
            <div key={s} className="surface-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{statusLabels[s]}</div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-2xl font-bold">{counts[s]}</span>
                <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-xs font-medium", statusStyles[s])}>
                  {statusLabels[s]}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plate, model or ID…" className="pl-9" aria-label="Search vehicles" />
            </div>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v)}>
                <SelectTrigger className="w-[140px]"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
                <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">In service</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <LoadingBlock label="Loading fleet…" />
          ) : (
            <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
              {filtered.map((v) => {
                const typeInfo = typesById.get(v.type_id);
                const Icon = typeIconFor(typeInfo?.name);
                const driver = v.current_driver_id ? driversById.get(v.current_driver_id) : undefined;
                const odometer = v.odometer_km ?? 0;
                const nextService = v.next_service_km;
                const serviceDue = nextService != null ? nextService - odometer : null;
                const dueSoon = serviceDue != null && serviceDue <= 1500;
                const title = [v.make, v.model].filter(Boolean).join(" ") || v.plate;
                return (
                  <article key={v.id} className="rounded-xl border p-4 transition hover:shadow-md">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="truncate font-semibold">{title}</h3>
                          <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium", statusStyles[v.status])}>
                            {statusLabels[v.status]}
                          </span>
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-muted-foreground">{v.plate} · {v.year ?? "—"}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {driver ? `Assigned to ${driver.name}` : "Unassigned"}
                        </div>
                      </div>
                    </div>

                    <dl className="mt-4 grid grid-cols-3 gap-2 rounded-lg bg-muted/40 p-3 text-center">
                      <div>
                        <dt className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <Gauge className="h-3 w-3" /> Odometer
                        </dt>
                        <dd className="mt-0.5 text-sm font-semibold">
                          {v.odometer_km != null ? `${v.odometer_km.toLocaleString()} km` : "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <Wrench className="h-3 w-3" /> Service
                        </dt>
                        <dd className={cn("mt-0.5 text-sm font-semibold", dueSoon && "text-warning-foreground")}>
                          {serviceDue == null ? "—" : serviceDue > 0 ? `${serviceDue.toLocaleString()} km` : "Due"}
                        </dd>
                      </div>
                      <div>
                        <dt className="flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          {v.fuel_type === "electric" ? <Zap className="h-3 w-3" /> : <Fuel className="h-3 w-3" />} Fuel
                        </dt>
                        <dd className="mt-0.5 text-sm font-semibold capitalize">{v.fuel_type ?? "—"}</dd>
                      </div>
                    </dl>

                    <div className={cn("mt-3 grid gap-2", isDriverOnly ? "grid-cols-1" : "grid-cols-4")}>
                      <Button variant="secondary" size="sm" asChild>
                        <Link to={`/app/vehicles/${v.id}`}>Details</Link>
                      </Button>
                      {isDriverOnly ? null : (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openEdit(v)}>
                            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => duplicate(v)}>
                            <Copy className="mr-1 h-3.5 w-3.5" /> Copy
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setToDelete(v)} className="text-destructive hover:text-destructive">
                            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
              {filtered.length === 0 ? (
                <div className="col-span-full p-10 text-center text-sm text-muted-foreground">
                  No vehicles match your filters.
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.plate}` : "Add vehicle"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update fleet details for this vehicle." : "Register a new vehicle in your operating fleet."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="plate">Plate number</Label>
              <Input id="plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} placeholder="LAG-284-KJA" />
            </div>
            <div>
              <Label htmlFor="make">Make</Label>
              <Input id="make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Honda" />
            </div>
            <div>
              <Label htmlFor="model">Model</Label>
              <Input id="model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="125" />
            </div>
            <div>
              <Label htmlFor="year">Year</Label>
              <Input id="year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type_id} onValueChange={(v) => setForm({ ...form, type_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fuel</Label>
              <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="petrol">Petrol</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cap">Capacity (kg)</Label>
              <Input id="cap" type="number" value={form.capacity_kg} onChange={(e) => setForm({ ...form, capacity_kg: Number(e.target.value) })} />
            </div>
            <div>
              <Label htmlFor="odo">Odometer (km)</Label>
              <Input id="odo" type="number" value={form.odometer_km} onChange={(e) => setForm({ ...form, odometer_km: Number(e.target.value) })} />
            </div>
            <div>
              <Label htmlFor="nsvc">Next service (km)</Label>
              <Input id="nsvc" type="number" value={form.next_service_km} onChange={(e) => setForm({ ...form, next_service_km: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v as VehicleStatus })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">In service</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isPersonal ? (
              <div className="sm:col-span-2">
                <Label>Company *</Label>
                <CompanyCombobox
                  value={form.corporate_account_id}
                  onChange={(v) => setForm({ ...form, corporate_account_id: v })}
                  companies={corporates}
                  disabled={isCorporate}
                />
              </div>
            ) : null}
            {!isPersonal ? (
            <div className="sm:col-span-2">
              <Label>Assigned driver</Label>
              <Select
                value={form.current_driver_id ?? "none"}
                onValueChange={(v) => setForm({ ...form, current_driver_id: v === "none" ? undefined : v })}
              >
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {drivers
                    .filter((d) => !form.corporate_account_id || d.corporate_account_id === form.corporate_account_id)
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Spinner className="mr-1.5" /> : null}
              {editing ? "Save changes" : "Add vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.plate}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the vehicle from your fleet view. Any active assignments will be unlinked. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
